<template>
  <div class="availability-date-section">
    <sp-select
      v-preserve-properties:items="modeItems"
      :disabled="disabled"
      class="availability-date-section__mode"
      :aria-label="prompt"
      :value.attr="mode"
      :prompt="prompt"
      :data-testid="TestId.finder.availabilityDate"
      @input="handleModeInput"
    ></sp-select>

    <AvailabilityTodayCard
      v-if="isTodayMode"
      :disabled="disabled"
      class="availability-date-section__view"
      :href="todayLinkHref"
    />

    <AvailabilityDayOfWeekPicker
      v-else-if="isDayOfWeekMode"
      :disabled="disabled"
      class="availability-date-section__view"
      :days="days"
      :label="dayOfWeekLabel"
      @toggle-day="handleToggleDay"
    />

    <AvailabilitySpecificDayPicker
      v-else-if="isSpecificDayMode"
      :disabled="disabled"
      class="availability-date-section__view"
      :date="date"
      :day-states="dayStates"
      :indicators="indicators"
      :label="selectDateLabel"
      @select="handleDateSelect"
      @visible-range="handleVisibleRange"
    />
  </div>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed } from "vue";
import { firstDetail } from "#ce/utils/events";
import { toBoolean, emptyToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { DATE_MODE, DATE_MODES } from "#ce/shared/filters/availability/composables/useAvailabilityFilterModel";
import { t } from "#ce/shared/filters/availability/i18n";
import AvailabilityDayOfWeekPicker from "#ce/shared/filters/availability/components/AvailabilityDayOfWeekPicker.ce.vue";
import AvailabilitySpecificDayPicker from "#ce/shared/filters/availability/components/AvailabilitySpecificDayPicker.ce.vue";
import AvailabilityTodayCard from "#ce/shared/filters/availability/components/AvailabilityTodayCard.ce.vue";

const emit = defineEmits(["update-mode", "select-date", "toggle-day", "visible-range"]);

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** The active date mode, one of `DATE_MODES`, or undefined. */
  mode: {
    type: String,
    default: undefined,
  },
  /** The selected `available_date`. */
  date: {
    type: String,
    default: undefined,
  },
  /** The selected weekday numbers. */
  days: {
    type: Array,
    default: () => [],
  },
  dayStates: {
    type: Object,
    default: () => ({}),
  },
  indicators: {
    type: Boolean,
    default: false,
  },
  todayLinkHref: {
    type: String,
    default: undefined,
  },
});

const prompt = computed(() => t("date_prompt"));

/** Each mode row's own `data-testid`, so a journey can name the mode it picks. */
const MODE_TEST_IDS = Object.freeze({
  [DATE_MODE.TODAY]: TestId.finder.availabilityDateToday,
  [DATE_MODE.DAY_OF_WEEK]: TestId.finder.availabilityDateDayOfWeek,
  [DATE_MODE.SPECIFIC_DAY]: TestId.finder.availabilityDateSpecificDay,
});

const modeItems = computed(() =>
  DATE_MODES.map((mode) => ({ title: t(`date_${mode}`), value: mode, props: { "data-testid": MODE_TEST_IDS[mode] } })),
);

const isTodayMode = computed(() => props.mode === DATE_MODE.TODAY);
const isDayOfWeekMode = computed(() => props.mode === DATE_MODE.DAY_OF_WEEK);
const isSpecificDayMode = computed(() => props.mode === DATE_MODE.SPECIFIC_DAY);

const dayOfWeekLabel = computed(() => t("date_day_of_week"));
const selectDateLabel = computed(() => t("select_date"));

function handleModeInput(event) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("update-mode", emptyToUndefined(firstDetail(event)));
}

function handleDateSelect(value) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("select-date", value);
}

function handleToggleDay(day, checked) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("toggle-day", day, checked);
}

function handleVisibleRange(range) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("visible-range", range);
}
</script>

<style lang="scss" scoped>
.availability-date-section {
  &__mode {
    display: block;
    width: 100%;
  }

  // The panel sets this root to `display: block`, so the mode's sub-view is spaced by margin. It
  // sits one field gap below the mode select, the same distance as between the panel's fields.
  &__view {
    margin-top: var(
      --sp-comp-availability-filter-date-view-gap,
      var(--sp-comp-availability-filter-field-gap, var(--sp-ref-scale-16))
    );
  }
}
</style>
